import { createSignal, onMount, Show, For } from "solid-js";
import Text from "../components/Text";
import styles from "./Goggles.module.css";

interface GogglesInfo {
    available: boolean;
    version: string | null;
    mcVersion: string;
    loader: string;
}

const FEATURES: [string, string, string][] = [
    ["fa-gamepad", "GOGGLES_F_POSSESS_TITLE", "GOGGLES_F_POSSESS_DESC"],
    ["fa-eye", "GOGGLES_F_SPECTATE_TITLE", "GOGGLES_F_SPECTATE_DESC"],
    ["fa-mountain-sun", "GOGGLES_F_TERRAIN_TITLE", "GOGGLES_F_TERRAIN_DESC"],
    ["fa-tags", "GOGGLES_F_OVERLAY_TITLE", "GOGGLES_F_OVERLAY_DESC"],
];

const STEPS: [string, string][] = [
    ["GOGGLES_STEP1_TITLE", "GOGGLES_STEP1_DESC"],
    ["GOGGLES_STEP2_TITLE", "GOGGLES_STEP2_DESC"],
    ["GOGGLES_STEP3_TITLE", "GOGGLES_STEP3_DESC"],
    ["GOGGLES_STEP4_TITLE", "GOGGLES_STEP4_DESC"],
];

const FALLBACK: GogglesInfo = { available: false, version: null, mcVersion: "1.8.9", loader: "Forge" };

export default function Goggles() {
    const [info, setInfo] = createSignal<GogglesInfo | null>(null);

    onMount(async () => {
        try {
            const res = await fetch("/api/goggles/info");
            setInfo(res.ok ? await res.json() : FALLBACK);
        } catch {
            setInfo(FALLBACK);
        }
    });

    const downloadBlock = () => (
        <Show
            when={info()?.available}
            fallback={
                <span class={styles.downloadPending}>
                    <i class="fa-solid fa-circle-notch fa-spin" />
                    <Text val="GOGGLES_PREPARING" />
                </span>
            }
        >
            <a class={styles.downloadBtn} href="/api/goggles/download" target="_blank" rel="external noopener">
                <i class="fa-solid fa-download" />
                <span class={styles.downloadLabel}><Text val="GOGGLES_DOWNLOAD" /></span>
                <Show when={info()?.version}>
                    <span class={styles.downloadVersion}>v{info()!.version}</span>
                </Show>
            </a>
        </Show>
    );

    return (
        <div class={styles.page}>
            <section class={styles.hero}>
                <div class={styles.heroGlow} />
                <div class={styles.heroGrid} />
                <div class={styles.heroInner}>
                    <p class={styles.brandTag}>
                        <i class="fa-solid fa-vr-cardboard" />
                        <Text val="GOGGLES_TAG" />
                    </p>
                    <h1 class={styles.heroTitle}><Text val="GOGGLES_HERO_TITLE" /></h1>
                    <p class={styles.heroSub}><Text val="GOGGLES_HERO_SUB" /></p>
                    <div class={styles.heroActions}>{downloadBlock()}</div>
                    <div class={styles.reqRow}>
                        <span class={styles.reqChip}><i class="fa-solid fa-cube" />Minecraft 1.8.9</span>
                        <span class={styles.reqChip}><i class="fa-solid fa-fire" />Forge</span>
                        <span class={styles.reqChip}><i class="fa-solid fa-bolt" /><Text val="GOGGLES_REQ_ACCOUNT" /></span>
                    </div>
                </div>
            </section>

            <section class={styles.section}>
                <div class={styles.sectionLabel}><span class={styles.dot} /><Text val="GOGGLES_WHAT_LABEL" /></div>
                <h2 class={styles.sectionTitle}><Text val="GOGGLES_WHAT_TITLE" /></h2>
                <div class={styles.featureGrid}>
                    <For each={FEATURES}>{([icon, title, desc]) => (
                        <div class={styles.featureCard}>
                            <div class={styles.featureIcon}><i class={`fa-solid ${icon}`} /></div>
                            <h3 class={styles.featureTitle}><Text val={title} /></h3>
                            <p class={styles.featureDesc}><Text val={desc} /></p>
                        </div>
                    )}</For>
                </div>
            </section>

            <section class={`${styles.section} ${styles.setupSection}`}>
                <div class={styles.sectionLabel}><span class={styles.dot} /><Text val="GOGGLES_SETUP_LABEL" /></div>
                <h2 class={styles.sectionTitle}><Text val="GOGGLES_SETUP_TITLE" /></h2>
                <div class={styles.steps}>
                    <For each={STEPS}>{([title, desc], i) => (
                        <div class={styles.step}>
                            <div class={styles.stepNum}>{i() + 1}</div>
                            <div class={styles.stepBody}>
                                <h3 class={styles.stepTitle}><Text val={title} /></h3>
                                <p class={styles.stepDesc}><Text val={desc} /></p>
                            </div>
                        </div>
                    )}</For>
                </div>
            </section>

            <section class={styles.ctaSection}>
                <div class={styles.ctaGlow} />
                <div class={styles.ctaInner}>
                    <h2 class={styles.ctaTitle}><Text val="GOGGLES_CTA_TITLE" /></h2>
                    <p class={styles.ctaDesc}><Text val="GOGGLES_CTA_DESC" /></p>
                    {downloadBlock()}
                </div>
            </section>
        </div>
    );
}
